<template>
  <div class="city-map-placeholder city-map-panel">
    <div v-if="loading" class="city-map-loading-state">
      <spinner />
      <div class="city-map-placeholder-subtitle">{{ adminText('workflowsAll.map_loading_6f502a5', 'Map loading...') }}</div>
    </div>
    <div v-else-if="error" class="city-map-loading-state">
      <div class="city-map-placeholder-subtitle">{{ workflowLabel(error) }}</div>
    </div>
    <gmap-map
      v-else-if="hasPoints"
      ref="citySalesMap"
      :center="cityMapCenter"
      :zoom="cityMapZoom"
      :options="workflowUi(cityMapOptions)"
      class="city-map-canvas"
    >
      <gmap-cluster
        :key="workflowLocale"
        :zoom-on-click="true"
        :styles="cityMapClusterStyles"
        :minimum-cluster-size="2"
        :calculator="clusterTicketsCalculator"
      >
        <gmap-marker
          v-for="point in points"
          :key="point.id"
          :position="{ lat: Number(point.latitude), lng: Number(point.longitude) }"
          :title="workflowLabel(String(Number(point.ticketsSold) || 0))"
          :icon="getCityMarkerIcon(point)"
          @click="handleCityMarkerClick(point)"
        />
      </gmap-cluster>
    </gmap-map>
    <div v-else class="city-map-loading-state">
      <div class="city-map-placeholder-subtitle">
        {{ adminText('workflowsAll.no_city_data_available_to_display_on_the_map_9314b38', 'No city data available to display on the map.') }}
      </div>
    </div>
  </div>
</template>

<script>
import WorkflowDisplay from '@/admin-i18n/workflows-all-display'
import GmapCluster from 'vue2-google-maps/dist/components/cluster'

export default {
  name: 'city-sales-map',
  components: {
    GmapCluster,
  },
  mixins: [WorkflowDisplay],
  props: {
    loading: {
      type: Boolean,
      default: false,
    },
    error: {
      type: String,
      default: '',
    },
    points: {
      type: Array,
      default: () => [],
    },
  },
  computed: {
    hasPoints() {
      return Array.isArray(this.points) && this.points.length > 0
    },
    cityMapCenter() {
      if (!this.hasPoints) return { lat: 45.1, lng: 15.2 }

      const sum = this.points.reduce(
        (acc, point) => ({
          lat: acc.lat + Number(point.latitude),
          lng: acc.lng + Number(point.longitude),
        }),
        { lat: 0, lng: 0 },
      )

      return {
        lat: sum.lat / this.points.length,
        lng: sum.lng / this.points.length,
      }
    },
    cityMapZoom() {
      if (!this.hasPoints) return 6
      if (this.points.length === 1) return 8

      let minLat = Number.POSITIVE_INFINITY
      let maxLat = Number.NEGATIVE_INFINITY
      let minLng = Number.POSITIVE_INFINITY
      let maxLng = Number.NEGATIVE_INFINITY

      this.points.forEach(point => {
        const lat = Number(point.latitude)
        const lng = Number(point.longitude)
        if (lat < minLat) minLat = lat
        if (lat > maxLat) maxLat = lat
        if (lng < minLng) minLng = lng
        if (lng > maxLng) maxLng = lng
      })

      const latSpan = Math.abs(maxLat - minLat)
      const lngSpan = Math.abs(maxLng - minLng)
      const maxSpan = Math.max(latSpan, lngSpan)

      if (maxSpan > 80) return 2
      if (maxSpan > 40) return 3
      if (maxSpan > 20) return 4
      if (maxSpan > 10) return 5

      return 6
    },
    cityMapClusterStyles() {
      return [
        {
          url: this.getClusterSvgDataUrl(64),
          width: 64,
          height: 64,
          textColor: '#00509B',
          textSize: 16,
          fontWeight: '700',
        },
        {
          url: this.getClusterSvgDataUrl(64),
          width: 64,
          height: 64,
          textColor: '#00509B',
          textSize: 16,
          fontWeight: '700',
        },
        {
          url: this.getClusterSvgDataUrl(64),
          width: 64,
          height: 64,
          textColor: '#00509B',
          textSize: 16,
          fontWeight: '700',
        },
      ]
    },
    cityMapOptions() {
      return {
        disableDefaultUI: true,
        zoomControl: true,
        streetViewControl: false,
        mapTypeControl: false,
        fullscreenControl: true,
        styles: [
          { elementType: 'geometry', stylers: [{ color: '#f8f8f8' }] },
          { elementType: 'labels.icon', stylers: [{ visibility: 'off' }] },
          { featureType: 'landscape', elementType: 'geometry', stylers: [{ color: '#ffffff' }] },
          { featureType: 'road', stylers: [{ visibility: 'off' }] },
          { featureType: 'poi', stylers: [{ visibility: 'off' }] },
          { featureType: 'transit', stylers: [{ visibility: 'off' }] },
          {
            featureType: 'administrative',
            elementType: 'geometry.stroke',
            stylers: [{ visibility: 'off' }],
          },
          {
            featureType: 'administrative',
            elementType: 'labels.text',
            stylers: [{ visibility: 'off' }],
          },
          {
            featureType: 'administrative.country',
            elementType: 'geometry.stroke',
            stylers: [{ visibility: 'on' }, { color: '#b9b9b9' }, { weight: 1.1 }],
          },
          {
            featureType: 'administrative.country',
            elementType: 'labels.text.fill',
            stylers: [{ visibility: 'on' }, { color: '#7a7a7a' }],
          },
          {
            featureType: 'administrative.country',
            elementType: 'labels.text.stroke',
            stylers: [{ visibility: 'on' }, { color: '#ffffff' }],
          },
          {
            featureType: 'water',
            elementType: 'geometry',
            stylers: [{ color: '#f0f0f0' }],
          },
          {
            featureType: 'water',
            elementType: 'labels.text.fill',
            stylers: [{ visibility: 'on' }, { color: '#8a8a8a' }],
          },
          {
            featureType: 'water',
            elementType: 'labels.text.stroke',
            stylers: [{ visibility: 'on' }, { color: '#ffffff' }],
          },
        ],
      }
    },
  },
  methods: {
    clusterTicketsCalculator(markers, numStyles) {
      const totalTickets = (markers || []).reduce((sum, marker) => {
        const markerValue = Number(marker && marker.getTitle && marker.getTitle()) || 0

        return sum + markerValue
      }, 0)

      return {
        text: String(totalTickets),
        index: Math.min(numStyles, 1),
        title: this.workflowLabel({ workflowParts: ['workflowsAll.tickets_sold_032746d', totalTickets, ''] }),
      }
    },
    getClusterSvgDataUrl(size) {
      const radius = size / 2
      const strokeWidth = 1
      const svg = `
        <svg xmlns="http://www.w3.org/2000/svg" width="${size}" height="${size}" viewBox="0 0 ${size} ${size}">
          <circle cx="${radius}" cy="${radius}" r="${
  radius - strokeWidth
}" fill="#AFC5D6" stroke="#4B9CF0" stroke-width="${strokeWidth}" />
        </svg>
      `

      return `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(svg.trim())}`
    },
    getMarkerSvgDataUrl(cityLabel, width) {
      const safeLabel = this.escapeSvgText(cityLabel)
      const markerWidth = width
      const markerHeight = 52
      const rectHeight = 30
      const dotRadius = 7
      const dotCenterY = 41
      const svg = `
        <svg xmlns="http://www.w3.org/2000/svg" width="${markerWidth}" height="${markerHeight}" viewBox="0 0 ${markerWidth} ${markerHeight}">
          <rect x="1" y="1" width="${
  markerWidth - 2
}" height="${rectHeight}" rx="9" ry="9" fill="#BFD5E5" stroke="#4B9CF0" stroke-width="1" />
          <text x="${
  markerWidth / 2
}" y="21" text-anchor="middle" font-family="Source Sans Pro, Arial, sans-serif" font-size="16" font-weight="500" fill="#00509B">${safeLabel}</text>
          <circle cx="${markerWidth / 2}" cy="${dotCenterY}" r="${dotRadius}" fill="#4B9CF0" />
        </svg>
      `

      return `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(svg.trim())}`
    },
    escapeSvgText(text) {
      return String(text)
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&#39;')
    },
    getCityMarkerIcon(point) {
      const city = point.city || point.postalCode || 'Unknown'
      const markerText = `${city} - ${Number(point.ticketsSold) || 0}`
      const textWidth = markerText.length * 8
      const markerWidth = Math.max(96, Math.min(220, textWidth + 20))

      return {
        url: this.getMarkerSvgDataUrl(markerText, markerWidth),
        size: { width: markerWidth, height: 52 },
        scaledSize: { width: markerWidth, height: 52 },
        anchor: { x: markerWidth / 2, y: 41 },
      }
    },
    async handleCityMarkerClick(point) {
      const lat = Number(point.latitude)
      const lng = Number(point.longitude)
      if (Number.isNaN(lat) || Number.isNaN(lng)) return

      const mapRef = this.$refs.citySalesMap
      const mapComponent = Array.isArray(mapRef) ? mapRef[0] : mapRef
      if (!mapComponent || !mapComponent.$mapPromise) return

      const map = await mapComponent.$mapPromise
      if (!map) return

      map.panTo({ lat, lng })

      const currentZoom = Number(map.getZoom()) || this.cityMapZoom || 0
      const targetZoom = Math.min(12, Math.max(currentZoom + 2, 8))
      map.setZoom(targetZoom)
    },
  },
}
</script>

<style scoped>
.city-map-placeholder {
  min-height: 360px;
  border-radius: 8px;
  border: 1px solid #f5f5f5;
  background: linear-gradient(180deg, #f5f5f5 0%, #ececec 100%);
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  text-align: center;
  padding: 16px;
}
.city-map-panel {
  padding: 0;
}
.city-map-trigger-btn {
  border: 1px solid #56a4ef;
  background: #cee4f2;
  color: #004c8c;
  border-radius: 4px;
  padding: 10px 16px;
  font-family: 'Source Sans Pro';
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
}
.city-map-loading-state {
  width: 100%;
  height: 100%;
  min-height: 360px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 12px;
  padding: 16px;
}
.city-map-canvas {
  width: 100%;
  height: 100%;
  min-height: 360px;
  border-radius: 8px;
}
.city-map-placeholder-subtitle {
  margin-top: 8px;
  color: #666;
  font-family: 'Source Sans Pro';
  font-size: 14px;
  line-height: 120%;
}
@media (max-width: 900px) {
  .city-map-placeholder,
  .city-map-loading-state,
  .city-map-canvas {
    min-height: 220px;
  }
}
</style>
